import { HomeCarousel } from "components/HomeCarousel";
import type { CarouselProps } from "components/HomeCarousel/HomeCarousel";
import { breakpointsInPx, useScreenViewport } from "lib/checkViewport";
import React, { isValidElement } from "react";
import { SwiperSlide } from "swiper/react";
import { useTrackViewed } from "tracking";
import type { Maybe } from "type-graphql";
import type { Tracking } from "@shared/graphql.types";
import { carouselModuleContainer } from "./CarouselModule.css";
import { colours } from "@shared/ui.design-system";

const defaultCarouselProps: CarouselProps = {
  gap: 20,
  arrowsShadow: false,
  arrowsColor: colours.primary.medium,
  prevBtnStyles: {
    left: "-5rem !important", // We are using !important to override <HomeCarousel> styles which do contain !important declaration as well
    border: `1px solid ${colours.secondary.deep} !important`,
    borderRadius: "2px"
  },
  nextBtnStyles: {
    right: "-5rem !important", // We are using !important to override <HomeCarousel> styles which do contain !important declaration as well
    border: `1px solid ${colours.secondary.deep} !important`,
    borderRadius: "2px"
  },
  containerProps: {
    pb: 1
  },
  arrowsLocation: "external",
  centeredSlides: false,
  loop: false,
  pagination: false,
  breakpoints: {
    [breakpointsInPx.lg]: {
      slidesPerView: 4,
      slidesPerGroup: 1
    },
    [breakpointsInPx.md]: {
      slidesPerView: 3,
      slidesPerGroup: 1
    },
    [breakpointsInPx.xs]: {
      slidesPerView: 2,
      slidesPerGroup: 1
    }
  },
  scrollbar: true
};

export const CarouselModule = ({
  children,
  tracking,
  carouselWrapperClassName = carouselModuleContainer,
  carouselProps = {}
}: {
  children: React.ReactNode[];
  tracking: Maybe<Tracking>;
  carouselWrapperClassName?: string;
  carouselProps?: CarouselProps;
}) => {
  const { isMobile } = useScreenViewport();
  const { ref } = useTrackViewed(tracking?.viewed, {
    threshold: 0.3,
    triggerOnce: true
  });

  const itemsLength = React.Children.count(children);

  const mergeCarouselProps: CarouselProps = {
    ...defaultCarouselProps,
    showNavigation: itemsLength > 4 && !isMobile,
    scrollbar: isMobile,
    ...carouselProps
  };

  return (
    <div
      ref={ref}
      className={carouselWrapperClassName ?? carouselModuleContainer}
    >
      <HomeCarousel {...mergeCarouselProps}>
        {[...children].map((child) => {
          if (!isValidElement(child)) {
            return null;
          }

          return (
            <SwiperSlide
              key={`${child.props?.children?.key}-swiper-slide`}
              className="swiper-slide-mobile"
              style={{
                height: "auto",
                width: "auto"
              }}
            >
              {child}
            </SwiperSlide>
          );
        })}
      </HomeCarousel>
    </div>
  );
};
